import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiShare } from 'react-icons/hi';
import { TbReportAnalytics } from 'react-icons/tb';
import { RiDeleteBin2Fill } from 'react-icons/ri';
import { useTranslate } from '../hooks/useTranslate';
import { useDataAdmission } from '../contexts/DataAdmissionContext';
import { useToast } from '../hooks/useToast';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import type { EnrichedSharedReport } from '../contexts/DataAdmissionContext';

interface SharedReportsSectionProps {
    className?: string;
    reports: EnrichedSharedReport[];
}

const SharedReportsSection: React.FC<SharedReportsSectionProps> = ({ className = '', reports }) => {
    const { t } = useTranslate();
    const navigate = useNavigate();
    const { deleteSharedReport } = useDataAdmission();
    const { push: pushToast } = useToast();
    const [pendingDelete, setPendingDelete] = useState<{ reportId: string; name: string } | null>(null);

    // Don't render section if there are no shared reports
    if (reports.length === 0) return null;

    const getFontStyles = () => ({ color: '#000000' });

    const formatDate = (dateStr: string) => {
        try {
            return new Date(dateStr).toLocaleDateString(undefined, {
                year: 'numeric', month: 'short', day: 'numeric'
            });
        } catch {
            return dateStr;
        }
    };

    const getHealthScoreColor = (score: string) => {
        const num = parseFloat(score);
        if (num >= 80) return 'bg-green-100 text-green-800';
        if (num >= 50) return 'bg-yellow-100 text-yellow-800';
        return 'bg-red-100 text-red-800';
    };

    return (
        <div className={`mt-32 ${className}`}>
            <div className="flex items-center mb-8 mt-16">
                <div className="flex items-center">
                    <h2 className="text-xl font-bold text-gray-800">
                        <HiShare className="inline mr-2 text-red-600 text-3xl" />
                        {t('Shared Reports')}
                    </h2>
                    <div className="ml-4 h-px bg-gradient-to-r from-red-300 to-transparent flex-1"></div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {reports.map((report) => (
                    <div
                        key={report.share_id}
                        className="rounded-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer transform hover:scale-105 hover:-translate-y-1 shadow-xl hover:shadow-red-500/20 relative overflow-hidden"
                        style={{
                            background: '#ffffff',
                            border: '2px solid #d1d5db',
                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                            transition: 'all 0.3s ease-out, box-shadow 0.3s ease-out',
                            minHeight: '79px'
                        }}
                        onClick={() => navigate(`/shared-report?reportId=${report.report_id}`)}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = '0 20px 40px -10px rgba(211, 17, 69, 0.4), 0 10px 25px -5px rgba(211, 17, 69, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                        }}
                    >
                        <div className="p-2">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center space-x-3 flex-1 min-w-0 pr-2">
                                    <div className="w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center flex-shrink-0">
                                        <TbReportAnalytics className="w-8 h-8 sm:w-12 sm:h-12 text-red-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-xs sm:text-sm font-semibold truncate notranslate" style={getFontStyles()}>
                                            {report.dataset_name || t('Shared Report')}
                                        </h3>
                                        <p className="text-[10px] sm:text-xs font-medium truncate" style={{ ...getFontStyles(), opacity: 0.6 }}>
                                            {t('Shared by')} {report.shared_by_email.split('@')[0]}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-1 flex-shrink-0">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setPendingDelete({
                                                reportId: report.report_id,
                                                name: report.dataset_name || t('Shared Report'),
                                            });
                                        }}
                                        className="p-1 hover:bg-red-500 hover:text-white rounded transition-all duration-200 flex-shrink-0"
                                        style={{ color: '#000000' }}
                                        title={t('Delete')}
                                    >
                                        <RiDeleteBin2Fill className="w-4 h-4 sm:w-5 sm:h-5" />
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center justify-end mt-0.5">
                                <span className="inline-flex items-center px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium bg-red-100 text-red-800 whitespace-nowrap">
                                    {report.issue_count} {t('issues')}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <DeleteConfirmationModal
                isOpen={!!pendingDelete}
                onClose={() => setPendingDelete(null)}
                onConfirm={async () => {
                    if (pendingDelete) {
                        try {
                            await deleteSharedReport(pendingDelete.reportId);
                            pushToast({ variant: 'success', message: t('Shared report removed'), ttl: 4000 });
                        } catch {
                            pushToast({ variant: 'error', message: t('Failed to delete shared report'), ttl: 4000 });
                        }
                        setPendingDelete(null);
                    }
                }}
                entityName={pendingDelete?.name || ''}
                title={t('Delete Shared Report')}
                confirmLabel={t('Delete Permanently')}
            />
        </div>
    );
};

export default SharedReportsSection;

